import { hasSpouse } from './validation';

/**
 * The step list is a FIXED constant — spouse steps are always present and are
 * skipped during navigation (and hidden in the sidebar) when the client has
 * no spouse or partner.
 *
 * This matters because `current_step` is persisted server-side as a plain
 * integer index and `total_steps` is a static value on the backend handler
 * (`PDS_TOTAL_STEPS` in backend/service_client_forms/forms/pds.py). If the
 * array grew and shrank with the spouse answer, a client parked on "Review"
 * who later switched to Yes would reopen on "Spouse Employment", and the
 * advisor's progress denominator would change underneath them.
 *
 * Consequence: never reorder or remove entries once clients have started the
 * form — only append.
 */
export const STEPS = [
  { key: 'welcome', label: 'Welcome', icon: 'bi-house-heart', person: null },
  { key: 'household', label: 'About You', icon: 'bi-people', person: null },
  { key: 'contact', label: 'Your Contact Info', icon: 'bi-person-vcard', person: 'primary' },
  { key: 'employment', label: 'Your Employment', icon: 'bi-briefcase', person: 'primary' },
  { key: 'compliance', label: 'Compliance & Reporting', icon: 'bi-file-earmark-check', person: 'primary' },
  { key: 'trusted_contact', label: 'Trusted Contact', icon: 'bi-shield-check', person: 'primary' },
  { key: 'beneficiaries', label: 'Beneficiaries', icon: 'bi-diagram-3', person: 'primary' },
  { key: 'spouse_contact', label: 'Spouse Contact Info', icon: 'bi-person-vcard', person: 'spouse' },
  { key: 'spouse_employment', label: 'Spouse Employment', icon: 'bi-briefcase', person: 'spouse' },
  { key: 'spouse_compliance', label: 'Spouse Compliance', icon: 'bi-file-earmark-check', person: 'spouse' },
  { key: 'spouse_trusted_contact', label: 'Spouse Trusted Contact', icon: 'bi-shield-check', person: 'spouse' },
  { key: 'spouse_beneficiaries', label: 'Spouse Beneficiaries', icon: 'bi-diagram-3', person: 'spouse' },
  { key: 'review', label: 'Review & Submit', icon: 'bi-check2-all', person: null },
];

export function isStepVisible(step, formData) {
  return step.person !== 'spouse' || hasSpouse(formData);
}

/**
 * Walk to the next (dir = +1) or previous (dir = -1) visible step index.
 * Returns STEPS.length when walking off the end, or -1 off the front.
 */
export function nextVisibleIndex(from, dir, formData) {
  let i = from + dir;
  while (i >= 0 && i < STEPS.length && !isStepVisible(STEPS[i], formData)) {
    i += dir;
  }
  return i;
}

/** Index of the last visible step — where the Submit button lives. */
export function lastVisibleIndex(formData) {
  return STEPS.reduce(
    (acc, step, idx) => (isStepVisible(step, formData) ? idx : acc),
    0,
  );
}

/** Number the client sees for a step, counted over visible steps only. */
export function visibleStepNumber(index, formData) {
  return STEPS
    .slice(0, index + 1)
    .filter((step) => isStepVisible(step, formData))
    .length - 1;
}

export function stepVisibility(formData) {
  return STEPS.map((step) => isStepVisible(step, formData));
}
